import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useCourse } from '../context/CourseContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Bookmark,
  BookmarkX,
  ArrowRight,
  Clock,
  Headphones,
} from 'lucide-react';

export const BookmarksPage = () => {
  const { chapters, bookmarks, toggleBookmark, getChapterProgress } = useCourse();

  const bookmarkedChapters = chapters.filter(ch => bookmarks.includes(ch.id));

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-silver-shimmer/10 py-12">
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-2xl mx-auto text-center mb-12"
        >
          <div className="w-16 h-16 rounded-full bg-maroon/10 flex items-center justify-center mx-auto mb-6 shadow-maroon">
            <Bookmark className="w-8 h-8 text-maroon" />
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-foreground mb-4">
            Your Bookmarks
          </h1>
          <p className="text-muted-foreground text-lg">
            Quick access to chapters you've saved for later
          </p>
        </motion.div>

        {/* Bookmarked Chapters */}
        {bookmarkedChapters.length > 0 ? (
          <div className="max-w-3xl mx-auto space-y-4">
            {bookmarkedChapters.map((chapter, index) => {
              const progress = getChapterProgress(chapter.id);
              return (
                <motion.div
                  key={chapter.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="overflow-hidden border border-silver/20 shadow-sm hover:shadow-silver transition-shadow">
                    <CardContent className="p-0">
                      <div className="flex">
                        {/* Image */}
                        <div className="w-32 sm:w-48 flex-shrink-0">
                          <img
                            src={chapter.image}
                            alt={chapter.title}
                            className="w-full h-full object-cover"
                          />
                        </div>

                        {/* Content */}
                        <div className="flex-1 p-4 sm:p-6 flex flex-col">
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <span className="text-xs font-semibold uppercase tracking-wider text-maroon">
                              {chapter.step}
                            </span>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="w-8 h-8 -mt-1 -mr-2 hover:text-maroon"
                              onClick={() => toggleBookmark(chapter.id)}
                            >
                              <BookmarkX className="w-4 h-4 text-muted-foreground" />
                            </Button>
                          </div>

                          <h3 className="font-display text-lg font-semibold text-foreground mb-1 line-clamp-1">
                            {chapter.title}
                          </h3>

                          <p className="text-sm text-muted-foreground mb-4 line-clamp-2 flex-1">
                            {chapter.summary}
                          </p>

                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {chapter.duration}
                              </span>
                              <span className="flex items-center gap-1">
                                <Headphones className="w-3 h-3" />
                                {chapter.audioLength}
                              </span>
                            </div>

                            <Link to={`/chapter/${chapter.id}`}>
                              <Button size="sm" className="btn-teal gap-1">
                                {progress.started ? 'Continue' : 'Start'}
                                <ArrowRight className="w-3 h-3" />
                              </Button>
                            </Link>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16"
          >
            <div className="w-20 h-20 rounded-full bg-silver/20 flex items-center justify-center mx-auto mb-6">
              <Bookmark className="w-10 h-10 text-silver" />
            </div>
            <h2 className="font-display text-xl font-semibold text-foreground mb-2">
              No Bookmarks Yet
            </h2>
            <p className="text-muted-foreground mb-6">
              Bookmark chapters to quickly access them later
            </p>
            <Link to="/chapters">
              <Button className="btn-maroon gap-2">
                Browse Chapters
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default BookmarksPage;
